'use client';

import { useEffect, useRef, useState } from 'react';
import { useSiteTheme } from '@/components/SiteThemeProvider';
import type { createBackgroundDog } from './backgroundDogRuntime';
import { createSceneRecovery } from './sceneRecovery';
import styles from './home-lab.module.css';

const desktopMedia = '(min-width: 1200px) and (min-height: 621px) and (orientation: landscape) and (hover: hover) and (pointer: fine)';

export default function LabBackgroundDog() {
  const hostRef = useRef<HTMLDivElement>(null);
  const runtime = useRef<ReturnType<typeof createBackgroundDog> | null>(null);
  const { theme } = useSiteTheme();
  const [desktop, setDesktop] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const latest = useRef({ visible, reducedMotion, theme });
  latest.current = { visible, reducedMotion, theme };
  const recovery = useRef<ReturnType<typeof createSceneRecovery> | null>(null);

  useEffect(() => {
    const media = window.matchMedia(desktopMedia), reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => { setDesktop(media.matches); setReducedMotion(reduced.matches); };
    const observer = new IntersectionObserver(entries => setVisible(entries.some(entry => entry.isIntersecting)), { threshold: .01 });
    if (hostRef.current) observer.observe(hostRef.current);
    update(); media.addEventListener('change', update); reduced.addEventListener('change', update);
    return () => { observer.disconnect(); media.removeEventListener('change', update); reduced.removeEventListener('change', update); };
  }, []);

  useEffect(() => {
    const controller = createSceneRecovery(() => setAttempt(value => value + 1));
    recovery.current = controller;
    const wake = () => { controller.setAvailable(desktop && latest.current.visible && !document.hidden); controller.wake(); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    hostRef.current?.addEventListener('webglcontextrestored', wake, true);
    const host = hostRef.current;
    wake();
    return () => {
      controller.dispose(); recovery.current = null;
      document.removeEventListener('visibilitychange', wake); window.removeEventListener('focus', wake);
      host?.removeEventListener('webglcontextrestored', wake, true);
    };
  }, [desktop]);

  useEffect(() => { recovery.current?.setAvailable(desktop && visible && !document.hidden); }, [desktop, visible]);

  useEffect(() => {
    if (!desktop) return;
    let cancelled = false;
    recovery.current?.setStatus('loading');
    void import('./backgroundDogRuntime').then(({ createBackgroundDog: create }) => {
      if (cancelled || !hostRef.current) return;
      runtime.current = create(hostRef.current, latest.current, () => recovery.current?.setStatus('error'));
      recovery.current?.setStatus('ready');
    }).catch(() => { if (!cancelled) recovery.current?.setStatus('error'); });
    return () => { cancelled = true; runtime.current?.dispose(); runtime.current = null; };
  }, [desktop, attempt]);

  useEffect(() => { runtime.current?.update({ visible, reducedMotion, theme }); }, [visible, reducedMotion, theme]);

  return <div ref={hostRef} className={styles.backgroundDog} data-lab-background-dog />;
}
